/**
 * Biometric Bridge
 * 
 * Handles biometric authentication.
 * PARTIAL on web - can use WebAuthn for passkey/biometric auth.
 */

import { BaseBridge } from '../BaseBridge'
import type { BridgeAvailability, BridgeCapabilities } from '../types'

interface BiometricResult {
  success: boolean
  authenticationType?: 'fingerprint' | 'face' | 'passkey' | 'unknown'
  error?: string
}

export class BiometricBridge extends BaseBridge {
  readonly bridgeName = 'Biometric'
  readonly availability: BridgeAvailability = 'partial'
  readonly methods = ['authenticate', 'isAvailable', 'getAvailableTypes']
  
  private supported = false
  
  async initialize(): Promise<void> {
    // Check if WebAuthn is available
    this.supported = 
      typeof window !== 'undefined' &&
      'PublicKeyCredential' in window &&
      typeof (PublicKeyCredential as any).isUserVerifyingPlatformAuthenticatorAvailable === 'function'
  }
  
  async handleCall(method: string, args: any[]): Promise<any> {
    switch (method) {
      case 'authenticate':
        return JSON.stringify(await this.authenticate(args[0]))
      case 'isAvailable':
        return await this.isAvailable()
      case 'getAvailableTypes':
        return JSON.stringify(await this.getAvailableTypes())
      default:
        throw this.createMethodNotFoundError(method)
    }
  }
  
  private async isAvailable(): Promise<boolean> {
    if (!this.supported) return false
    
    try {
      return await (PublicKeyCredential as any).isUserVerifyingPlatformAuthenticatorAvailable()
    } catch {
      return false
    }
  }
  
  private async getAvailableTypes(): Promise<string[]> {
    const available = await this.isAvailable()
    
    if (!available) {
      return []
    }
    
    // WebAuthn doesn't tell us specific biometric type
    return ['passkey']
  }
  
  private async authenticate(_reason?: string): Promise<BiometricResult> {
    if (!this.supported) {
      return {
        success: false,
        error: 'Biometric authentication not supported in this browser'
      }
    }
    
    const available = await this.isAvailable()
    if (!available) {
      return {
        success: false,
        error: 'No biometric authenticator available'
      }
    }
    
    try {
      // Use WebAuthn for authentication
      // This is a simplified implementation - real usage would need proper
      // credential management with a server
      
      // For now, we'll use a simple challenge-response
      const challenge = new Uint8Array(32)
      crypto.getRandomValues(challenge)
      
      const credential = await navigator.credentials.get({
        publicKey: {
          challenge,
          timeout: 60000,
          userVerification: 'required',
          rpId: window.location.hostname,
          allowCredentials: []
        }
      })
      
      if (credential) {
        return {
          success: true,
          authenticationType: 'passkey'
        }
      }
      
      return {
        success: false,
        error: 'Authentication cancelled'
      }
      
    } catch (err: any) {
      if (err.name === 'NotAllowedError') {
        return {
          success: false,
          error: 'Authentication was cancelled or not allowed'
        }
      }
      
      if (err.name === 'NotSupportedError') {
        return {
          success: false,
          error: 'This authentication method is not supported'
        }
      }
      
      return {
        success: false,
        error: err.message || 'Authentication failed'
      }
    }
  }
  
  getCapabilities(): BridgeCapabilities {
    return {
      authenticate: { 
        available: this.supported,
        fallback: 'Uses WebAuthn/Passkey - may not have native biometric feel',
        requiresPermission: true
      },
      isAvailable: { available: true },
      getAvailableTypes: { available: true }
    }
  }
}

